// Theme — light only (single theme). Inspired by opencode.ai:
// warm white background, BLACK accent (not blue), hairlines.
//
// Shape/wire tone ~1 step darker than background — model stands out on canvas.
// Thin grid lines, default OFF (R15).
// Export `applyTheme` so main.ts does not need to know color tokens.

// @ts-ignore
import * as THREE from 'three';

export interface ThemePalette {
  bg: string;            // canvas background
  shape: number;         // mesh color (hex int)
  wire: number;          // wireframe color (int)
  gridCenter: number;    // grid center line
  gridMain: number;      // grid main lines
  ambient: number;       // ambient light intensity
  directional: number;   // directional light intensity
}

const LIGHT_PALETTE: ThemePalette = {
  bg: '#ffffff',         // R21 (2026-09-22): pure white per user request (was #fafaf7 off-white)
  shape: 0xa8a8b0,       // R21: light gray (lighter than old #5a5a62) — soft model on white
  wire: 0x1a1a1a,        // black wire (matches opencode BLACK heading)
  gridCenter: 0xa8a8a0,  // neutral
  gridMain: 0xe8e8e0,    // light hairline — does not overpower model
  ambient: 0.6,
  directional: 0.85,
};

// Clean hierarchical grid: 
// 1. Crisp near, fading far (distance fade-out shader)
// 2. Major grid slightly stronger, minor grid lighter
export function createThinGrid(size = 500, divisions = 100): THREE.Group {
  const group = new THREE.Group();

  // Custom shader material for the 3D axis grid (fully crisp near, faded far)
  const createGridShaderMat = (colorHex: number, defaultOpacity: number, isMajor: boolean) => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
      uniforms: {
        uColor: { value: new THREE.Color(colorHex) },
        uOpacity: { value: defaultOpacity },
      },
      vertexShader: `
        varying float vDistance;
        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vDistance = length(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uOpacity;
        varying float vDistance;
        void main() {
          // Distant grid fades out (starts fading at 1/4 of 350mm -> 1200mm range)
          float fade = 1.0 - smoothstep(350.0, 1200.0, vDistance);
          gl_FragColor = vec4(uColor, uOpacity * fade);
        }
      `,
    });
  };

  // 1. Minor grid — 5mm cells, half strength (opacity 0.32 instead of 0.65)
  const minorMat = createGridShaderMat(0x94a3b8, 0.16, false);
  const minorGrid = new THREE.GridHelper(size, divisions, 0x94a3b8, 0x94a3b8);
  minorGrid.material = minorMat as any;
  minorGrid.renderOrder = -1;
  group.add(minorGrid);

  // 2. Major grid — 25mm cells, half strength (opacity 0.42 instead of 0.85), CAD standard
  const majorMat = createGridShaderMat(0x64748b, 0.22, true);
  const majorGrid = new THREE.GridHelper(size, 20, 0x64748b, 0x64748b);
  majorGrid.material = majorMat as any;
  majorGrid.renderOrder = -1;
  group.add(majorGrid);

  return group as any;
}

export function applyTheme(scene: THREE.Scene, mesh: THREE.Mesh | null = null, wire: THREE.LineSegments | null = null): void {
  scene.background = new THREE.Color(LIGHT_PALETTE.bg);
  if (wire) (wire.material as THREE.LineBasicMaterial).color.set(LIGHT_PALETTE.wire);
}

export const PALETTE = LIGHT_PALETTE;
